import { type ChangeEvent, useRef, useState } from 'react';

import { errorMessage } from '@/shared/api/errors';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { Dialog } from '@/shared/ui/Dialog';
import { Select } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { cn } from '@/shared/utils/cn';

import { SignatureEditor } from './SignatureEditor';
import {
  cleanForEditor,
  forScreen,
  importOutlook,
  isEmpty,
  MAIL_LOOK,
  outlookSignatures,
  type PickedFile,
  picked,
  signatureName,
} from './signatureHtml';
import {
  type Signature,
  type Signatures,
  useRemoveSignature,
  useSaveSignature,
  useSignatures,
} from './useSignatures';

/** The row of «every mailbox», and the key the editor uses for it. */
const EVERY = '';

/**
 * «Firma con formato» (contract v10.57, ADR 0054 of the back): the signature as Outlook
 * sends it, with its logo and its legal notice, one per mailbox the person reads and one
 * for all of them. The mailbox's own wins; without any, the plain text one below (v10.41).
 *
 * A back without v10.57 answers 404 and the card is not there.
 */
export function SignaturesCard() {
  const signatures = useSignatures();
  const [editing, setEditing] = useState<string | null>(null);

  if (signatures.isError && signatures.error.status === 404) return null;

  return (
    <Card
      title="Firma con formato"
      description="Como la de Outlook: con el logo, los colores y el aviso legal. Va debajo de lo que escribes en cada correo que sale desde aquí."
      className="max-w-3xl"
    >
      {signatures.isPending ? (
        <Spinner variant="block" label="Cargando tus firmas" />
      ) : signatures.isError ? (
        <p role="alert" className="text-danger text-[13.5px]">
          {errorMessage(signatures.error)}
        </p>
      ) : (
        <SignatureRows data={signatures.data} onEdit={setEditing} />
      )}
      {editing !== null && signatures.data ? (
        <SignatureDialog
          mailbox={editing}
          data={signatures.data}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Card>
  );
}

function rowsOf(
  data: Signatures,
): { key: string; label: string; signature: Signature | undefined }[] {
  const byMailbox = new Map((data.signatures ?? []).map((each) => [each.mailbox ?? EVERY, each]));
  const mailboxes = data.mailboxes ?? [];
  return [
    {
      key: EVERY,
      label: mailboxes.length > 1 ? 'Todos tus buzones' : 'Tu firma',
      signature: byMailbox.get(EVERY),
    },
    ...(mailboxes.length > 1
      ? mailboxes.map((each) => ({
          key: each.mailbox,
          label: each.address,
          signature: byMailbox.get(each.mailbox),
        }))
      : []),
  ];
}

function SignatureRows({ data, onEdit }: { data: Signatures; onEdit: (key: string) => void }) {
  const remove = useRemoveSignature();
  const rows = rowsOf(data);
  const general = rows[0]?.signature;
  return (
    <div className="flex flex-col gap-4">
      {rows.map((row) => (
        <div key={row.key} className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-ink text-[14px] font-semibold">{row.label}</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => onEdit(row.key)}>
                {row.signature ? 'Cambiar' : 'Poner firma'}
              </Button>
              {row.signature ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(row.key || null)}
                >
                  Quitar
                </Button>
              ) : null}
            </div>
          </div>
          {row.signature ? (
            <SignaturePreview html={row.signature.html} />
          ) : (
            <p className="text-ink-3 text-[13.5px]">
              {row.key === EVERY
                ? data.plain
                  ? 'Sin firma con formato: sale tu firma de texto.'
                  : 'Sin firma.'
                : general
                  ? 'Sale la de todos tus buzones.'
                  : data.plain
                    ? 'Sale tu firma de texto.'
                    : 'Sin firma.'}
            </p>
          )}
        </div>
      ))}
      {remove.isError ? (
        <p role="alert" className="text-danger text-[13.5px]">
          {errorMessage(remove.error)}
        </p>
      ) : null}
    </div>
  );
}

/** A saved signature as it will arrive. The back cleaned it; this cleans it again. */
export function SignaturePreview({ html, className }: { html: string; className?: string }) {
  return (
    <div
      className={cn('border-line overflow-x-auto rounded-md border p-3', MAIL_LOOK, className)}
      // Cleaned twice: by the back when it was saved and here.
      dangerouslySetInnerHTML={{ __html: forScreen(cleanForEditor(html)) }}
    />
  );
}

function SignatureDialog({
  mailbox,
  data,
  onClose,
}: {
  mailbox: string;
  data: Signatures;
  onClose: () => void;
}) {
  const save = useSaveSignature();
  const current = (data.signatures ?? []).find((each) => (each.mailbox ?? EVERY) === mailbox);
  const [initial, setInitial] = useState(current?.html ?? '');
  const [html, setHtml] = useState(current?.html ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [found, setFound] = useState<{ htm: PickedFile[]; files: PickedFile[] } | null>(null);
  const folder = useRef<HTMLInputElement>(null);
  const others = (data.signatures ?? []).filter((each) => (each.mailbox ?? EVERY) !== mailbox);
  const label = rowsOf(data).find((row) => row.key === mailbox)?.label ?? mailbox;

  function load(next: string): void {
    // A new `initial` repaints the editor, even when it is the same text as before.
    setInitial(`${next}<!--${Date.now()}-->`);
    setHtml(next);
  }

  async function bring(htm: PickedFile, files: PickedFile[]): Promise<void> {
    setFound(null);
    const imported = await importOutlook(htm, files);
    load(imported.html);
    setProblem(
      imported.missing > 0
        ? `${imported.missing === 1 ? 'Una imagen no estaba' : `${imported.missing} imágenes no estaban`} en la carpeta de la firma. Puedes añadirlas con «Imagen».`
        : null,
    );
  }

  function pickFolder(event: ChangeEvent<HTMLInputElement>): void {
    const files = picked(event.target.files ?? []);
    event.target.value = '';
    const htm = outlookSignatures(files);
    if (htm.length === 0) {
      setProblem(
        'En esa carpeta no hay ninguna firma de Outlook (un fichero .htm). Es la carpeta «Signatures».',
      );
      return;
    }
    const [first] = htm;
    if (htm.length === 1 && first) void bring(first, files);
    else setFound({ htm, files });
  }

  function submit(): void {
    setNotes([]);
    save.mutate(
      { mailbox: mailbox || null, html },
      {
        onSuccess: (saved) => {
          if (saved.notes && saved.notes.length > 0) {
            setNotes(saved.notes);
            load(saved.html);
          } else {
            onClose();
          }
        },
      },
    );
  }

  const empty = isEmpty(html);
  return (
    <Dialog
      title={`Firma: ${label}`}
      size="lg"
      busy={save.isPending}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            {notes.length > 0 ? 'Cerrar' : 'Cancelar'}
          </Button>
          <Button onClick={submit} disabled={save.isPending || empty}>
            {save.isPending ? 'Guardando…' : 'Guardar la firma'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-ink-2 text-[13.5px]">
          Escríbela aquí, pégala desde Outlook o tráela de la carpeta de firmas de Outlook. Las
          imágenes viajan dentro del correo, como en Outlook.
        </p>
        <SignatureEditor
          initial={initial}
          onChange={setHtml}
          onProblem={setProblem}
          toolbarExtra={
            <>
              <button
                type="button"
                className="text-accent hover:bg-paper-2 h-8 rounded px-2 text-[13px] font-medium"
                onClick={() => folder.current?.click()}
              >
                Traer de Outlook
              </button>
              <input
                ref={folder}
                type="file"
                className="hidden"
                aria-label="Carpeta de firmas de Outlook"
                multiple
                // The `.htm` and the folder of its images: only a folder brings both.
                {...{ webkitdirectory: '', directory: '' }}
                onChange={pickFolder}
              />
            </>
          }
        />
        <p className="text-ink-3 text-[12.5px] leading-snug">
          «Traer de Outlook»: elige la carpeta <strong>Signatures</strong>. En Windows está en{' '}
          <code className="text-[12px]">%APPDATA%\Microsoft\Signatures</code> (pégalo en la barra
          del explorador de archivos).
        </p>
        {found ? (
          <Select
            label="¿Qué firma traes?"
            defaultValue=""
            onChange={(event) => {
              const chosen = found.htm.find((each) => each.path === event.target.value);
              if (chosen) void bring(chosen, found.files);
            }}
          >
            <option value="">Elige una…</option>
            {found.htm.map((each) => (
              <option key={each.path} value={each.path}>
                {signatureName(each)}
              </option>
            ))}
          </Select>
        ) : null}
        {others.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 text-[13px]">
            <span className="text-ink-3">Empezar con la de:</span>
            {others.map((each) => (
              <Button key={each.id} size="sm" variant="ghost" onClick={() => load(each.html)}>
                {rowsOf(data).find((row) => row.key === (each.mailbox ?? EVERY))?.label}
              </Button>
            ))}
          </div>
        ) : null}
        {problem ? (
          <p role="alert" className="text-warn text-[13.5px]">
            {problem}
          </p>
        ) : null}
        {notes.length > 0 ? (
          <Callout tone="warn" title="Guardada, con cambios">
            <ul className="list-disc pl-5">
              {notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </Callout>
        ) : null}
        {save.isError ? (
          <p role="alert" className="text-danger text-[13.5px]">
            {errorMessage(save.error)}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
